import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import SupplementsTable from '../../SupplementsTable';
import { r, color, radius } from '../../../theme/tokens';
import { useNavigate } from 'react-router-dom';

/**
 * The citations map SupplementsTable expects, keyed by supplementId.
 *
 * Stacks store citations at the stack level (`stack.citations`), which is what
 * every generator writes and what /view-stack reads. This modal used to read
 * only `supplement.citations` off each row, a shape no writer produces, so its
 * "N studies" chips were always empty. Rows are still read as a fallback for
 * supplements the stack-level map does not cover. Where both have an entry,
 * the stack-level one is used.
 */
export const resolveStackCitations = (stack) => {
  const resolved = {};
  (stack?.supplements || []).forEach((supplement) => {
    const id = supplement.supplementId || supplement.key;
    if (id && supplement.citations) resolved[id] = supplement.citations;
  });
  return { ...resolved, ...(stack?.citations || {}) };
};

const ViewStackModal = ({
  open,
  onClose,
  stack,
}) => {
  const navigate = useNavigate();
  const citations = useMemo(() => resolveStackCitations(stack), [stack]);
  const [localSupplements, setLocalSupplements] = useState([]);

  // Handle supplement link clicks
  const handleMainLinkClick = (supplementId, e) => {
    e.preventDefault();
    navigate(`/supplements/${supplementId}`);
  };

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date || !(date instanceof Date) || date.getTime() === 0) return 'No date'; 
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    try {
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  // Initialize local supplements from stack data
  useEffect(() => {
    if (stack?.supplements) {
      // Initialize local supplements state with stack supplements
      // This allows interaction data to be added without modifying parent state
      setLocalSupplements(stack.supplements);
    } else {
      // Clear local state when modal closes (stack becomes null)
      setLocalSupplements([]);
    }
  }, [stack]);

  if (!stack) {
    return null;
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '1rem',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* No icon in the title. Ruled 2026-08-20: the shield that used to
              sit here is gone, and nothing decorative replaces it. */}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stack.name || (stack.systemStackType === 'diet_support' ? 'Nourish Stack' : 'Replenish Stack')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Stack Info */}
        <Box sx={{ mb: 3 }}>

          {/* Removed supplements count text for cleaner view */}

          {stack.jsUpdatedAt && (
            <Typography variant="body2" color="text.secondary">
              Last updated: {formatDateForDisplay(stack.jsUpdatedAt)}
            </Typography>
          )}

          {stack.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Notes: {stack.notes}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Supplements Table - Constrained width for better desktop layout */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box sx={{
            width: '100%',
            maxWidth: { xs: '100%', md: '56.25rem' },
            '& .MuiPaper-root': {
              boxShadow: 'none',
              borderRadius: radius.md,
              border: `${r(1)} solid ${color.line}`
            }
          }}>
            <SupplementsTable
              selectedSupplements={localSupplements}
              setSelectedSupplements={setLocalSupplements} // Allow interaction data updates
              citations={citations}
              inDialog // Lifts the studies drawer above this Dialog
              getStackId={() => stack.id}
              handleMainLinkClick={handleMainLinkClick}
              handleDrawerLinkClick={() => {}} // No-op for read-only
              pendingDrawerRestoreInfo={null}
              onDrawerRestored={() => {}}
              stackVersion={stack.version}
              readOnly={true} // This is the key prop for read-only mode
              // Same gate as /view-stack/:id — analysed stacks keep both chips.
              showAnalysis={true}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewStackModal; 